import React, { useEffect, useState } from "react";
import { Plus, FlaskConical, Play, X, Sparkles } from "lucide-react";
import LabShell from "./LabShell";
import { Panel, StatCard, StatusPill, EmptyState, formatRelative, formatDuration } from "./labUi";
import {
    fetchRuns,
    fetchInbox,
    stopRun,
    normalizeRunStatus,
    isBackendUnavailable,
} from "../../services/LabService";
import { labHref, navigate } from "./labRouter";
import { useLabHealth } from "./LabAccessGate";

const useDashboardData = () => {
    const [state, setState] = useState({ loading: true, error: null, running: [], succeeded: [], pending: [] });

    const reload = async () => {
        try {
            const [runningRes, succeededRes, pendingRes] = await Promise.all([
                fetchRuns({ status: "RUNNING", limit: 25 }).catch((e) => (isBackendUnavailable(e) ? { items: [] } : Promise.reject(e))),
                fetchRuns({ status: "SUCCEEDED", limit: 5 }).catch((e) => (isBackendUnavailable(e) ? { items: [] } : Promise.reject(e))),
                fetchInbox({ status: "pending", limit: 10 }).catch((e) => (isBackendUnavailable(e) ? { items: [] } : Promise.reject(e))),
            ]);
            setState({
                loading: false,
                error: null,
                running: runningRes?.items || [],
                succeeded: succeededRes?.items || [],
                pending: pendingRes?.items || [],
            });
        } catch (error) {
            setState((s) => ({ ...s, loading: false, error }));
        }
    };

    useEffect(() => {
        reload();
        const interval = setInterval(reload, 10_000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { ...state, reload };
};

const LiveRunRow = ({ run, onCancel }) => {
    const uiStatus = normalizeRunStatus(run.status);
    return (
        <li className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
            <div className="min-w-0 flex-1">
                <a
                    href={labHref(`experiments/${run.run_id}`)}
                    onClick={(e) => { e.preventDefault(); navigate(labHref(`experiments/${run.run_id}`)); }}
                    className="text-sm font-medium text-slate-900 dark:text-slate-50 hover:text-indigo-600 dark:hover:text-indigo-300 truncate block"
                >
                    {run.proposal_id || run.run_id}
                </a>
                <div className="flex items-center gap-2 text-xs text-slate-500 mt-0.5">
                    <StatusPill status={uiStatus}>
                        {run.current_state || uiStatus}
                    </StatusPill>
                    <span>started {formatRelative(run.started_at)}</span>
                </div>
            </div>
            {uiStatus === "running" ? (
                <button
                    onClick={() => onCancel(run.run_id)}
                    title="Stop run"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                >
                    <X className="w-3.5 h-3.5" /> Stop
                </button>
            ) : null}
        </li>
    );
};

const PendingRow = ({ item }) => (
    <li className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
        <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-slate-900 dark:text-slate-50 truncate">
                {item.title || item.proposal_id}
            </div>
            <div className="text-xs text-slate-500 mt-0.5 font-mono">
                {item.proposal_id} · {item.scenario_count ?? "?"} scenarios · ~${item.cost_estimate_usd?.toFixed?.(2) || "—"}
            </div>
        </div>
        <StatusPill status="queued" />
    </li>
);

const RecentReportRow = ({ run }) => (
    <li className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
        <div className="min-w-0 flex-1">
            <a
                href={labHref(`experiments/${run.run_id}`)}
                onClick={(e) => { e.preventDefault(); navigate(labHref(`experiments/${run.run_id}`)); }}
                className="text-sm font-medium text-slate-900 dark:text-slate-50 hover:text-indigo-600 dark:hover:text-indigo-300 truncate block"
            >
                {run.proposal_id || run.run_id}
            </a>
            <div className="text-xs text-slate-500 mt-0.5">
                Done {formatRelative(run.stopped_at)} · took {formatDuration((run.duration_seconds || 0) * 1000)}
            </div>
        </div>
        <StatusPill status="done" />
    </li>
);

const LabDashboard = () => {
    const health = useLabHealth();
    const { loading, error, running, succeeded, pending, reload } = useDashboardData();

    const handleCancel = async (runId) => {
        if (!window.confirm("Stop this run? It can't be resumed.")) return;
        await stopRun(runId).catch((err) => alert(err.message));
        reload();
    };

    const newExperimentButton = (
        <button
            onClick={() => navigate(labHref("submit"))}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm bg-indigo-600 hover:bg-indigo-700 text-white"
        >
            <Plus className="w-4 h-4" /> New experiment
        </button>
    );

    const pipeline = health?.pipeline;
    const endpoint = health?.endpoint;

    return (
        <LabShell activeKey="dashboard" headerAction={newExperimentButton}>
            {error ? (
                <div className="mb-4 rounded-lg border border-red-200 bg-red-50 dark:bg-red-950/30 dark:border-red-900 p-3 text-sm text-red-700 dark:text-red-300">
                    {error.message}
                </div>
            ) : null}

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                <StatCard
                    label="Inbox pending"
                    value={pipeline?.inbox_pending_count ?? "—"}
                    hint="Proposals waiting to be claimed"
                />
                <StatCard
                    label="Running now"
                    value={pipeline?.currently_running_executions ?? running.length}
                    hint={pipeline?.paused ? "Pipeline paused" : "Live"}
                />
                <StatCard
                    label="Done last 24h"
                    value={pipeline?.inbox_done_count_24h ?? "—"}
                    hint={endpoint?.status ? `Endpoint: ${endpoint.status} (×${endpoint.current_instances ?? 0})` : null}
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Panel
                    title={
                        <span className="flex items-center gap-2">
                            <Play className="w-4 h-4 text-blue-500" /> Live runs
                        </span>
                    }
                    padded={false}
                >
                    {running.length === 0 ? (
                        <EmptyState
                            icon={<Play className="w-6 h-6" />}
                            title="Nothing running"
                            body={pending.length > 0
                                ? `${pending.length} proposal${pending.length === 1 ? "" : "s"} waiting to be claimed.`
                                : "Submit a new experiment to see the pipeline in action."}
                        />
                    ) : (
                        <ul>
                            {running.map((run) => (
                                <LiveRunRow key={run.run_id} run={run} onCancel={handleCancel} />
                            ))}
                        </ul>
                    )}
                </Panel>

                <Panel title="Recent reports" padded={false}>
                    {succeeded.length === 0 ? (
                        <EmptyState
                            icon={<FlaskConical className="w-6 h-6" />}
                            title="No completed runs yet"
                            body="Your runs will appear here once they finish."
                        />
                    ) : (
                        <ul>
                            {succeeded.map((run) => (
                                <RecentReportRow key={run.run_id} run={run} />
                            ))}
                        </ul>
                    )}
                </Panel>
            </div>

            {pending.length > 0 ? (
                <div className="mt-6">
                    <Panel
                        title={
                            <span className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-amber-500" /> Pending inbox ({pending.length})
                            </span>
                        }
                        padded={false}
                    >
                        <ul>
                            {pending.slice(0, 5).map((item) => (
                                <PendingRow key={item.proposal_id} item={item} />
                            ))}
                        </ul>
                    </Panel>
                </div>
            ) : null}

            {!loading && running.length === 0 && succeeded.length === 0 && pending.length === 0 ? (
                <div className="mt-6">
                    <Panel>
                        <EmptyState
                            icon={<FlaskConical className="w-8 h-8" />}
                            title="Welcome to the Lab"
                            body="You haven't run any experiments yet. Submit a proposal to claim the next pipeline run."
                            action={
                                <button
                                    onClick={() => navigate(labHref("submit"))}
                                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-700 text-white text-sm"
                                >
                                    <Plus className="w-4 h-4" /> Start your first experiment
                                </button>
                            }
                        />
                    </Panel>
                </div>
            ) : null}
        </LabShell>
    );
};

export default LabDashboard;
